/**
 * One pooled billboard field = one draw call. Used for every particle in the game:
 * coin-fountain sparkle, Magnet spark ends, Geist wisps, Fluch smolder, dust, glass.
 *
 * Two modes share the buffer:
 *   · simulated  — spawn() gives a particle a life, velocity, gravity and drag;
 *   · immediate  — push() writes a quad for this frame only (attached FX like wisps).
 */

import {
  InstancedMesh, PlaneGeometry, MeshBasicMaterial, AdditiveBlending, NormalBlending,
  InstancedBufferAttribute, Object3D, Color, Matrix4, Quaternion, Vector3,
  type Texture, type Camera, type Blending, type Scene,
} from 'three';

export interface SpawnOpts {
  pos: Vector3 | [number, number, number];
  vel?: Vector3 | [number, number, number];
  gravity?: number;
  drag?: number;
  life?: number;
  size?: number;
  sizeEnd?: number;
  color?: number | Color;
  colorEnd?: number | Color;
  /** 0 = billboard, 1 = stretched along velocity (sparks, coin arcs) */
  stretch?: number;
  fade?: 'linear' | 'inOut' | 'hold';
}

interface P {
  alive: boolean;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  g: number; drag: number;
  t: number; life: number;
  s0: number; s1: number;
  c0: Color; c1: Color;
  stretch: number;
  fade: string;
}

const _m = new Matrix4();
const _q = new Quaternion();
const _pos = new Vector3();
const _scl = new Vector3();
const _col = new Color();
const _dummy = new Object3D();

export class SpriteField {
  mesh: InstancedMesh;
  private pool: P[] = [];
  private immediate: { x: number; y: number; z: number; s: number; c: Color; rot: number }[] = [];
  private aCol: InstancedBufferAttribute;
  private cap: number;

  constructor(texture: Texture, capacity = 512, blending: Blending = AdditiveBlending, opts: { depthWrite?: boolean } = {}) {
    this.cap = capacity;
    const geo = new PlaneGeometry(1, 1);
    const mat = new MeshBasicMaterial({
      map: texture, transparent: true, blending,
      depthWrite: opts.depthWrite ?? false, toneMapped: false,
    });
    this.mesh = new InstancedMesh(geo, mat, capacity);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.userData.noInk = true;
    this.mesh.renderOrder = blending === NormalBlending ? 4 : 6;
    const cols = new Float32Array(capacity * 3).fill(1);
    this.aCol = new InstancedBufferAttribute(cols, 3);
    this.mesh.instanceColor = this.aCol;
    for (let i = 0; i < capacity; i++) {
      this.pool.push({
        alive: false, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, g: 0, drag: 0,
        t: 0, life: 1, s0: 1, s1: 1, c0: new Color(1, 1, 1), c1: new Color(0, 0, 0),
        stretch: 0, fade: 'linear',
      });
    }
  }

  addTo(scene: Scene | Object3D): this { scene.add(this.mesh); return this; }

  spawn(o: SpawnOpts): void {
    const p = this.pool.find((q) => !q.alive);
    if (!p) return;
    const pos = Array.isArray(o.pos) ? o.pos : [o.pos.x, o.pos.y, o.pos.z];
    const vel = o.vel ? (Array.isArray(o.vel) ? o.vel : [o.vel.x, o.vel.y, o.vel.z]) : [0, 0, 0];
    p.alive = true;
    p.x = pos[0]; p.y = pos[1]; p.z = pos[2];
    p.vx = vel[0]; p.vy = vel[1]; p.vz = vel[2];
    p.g = o.gravity ?? 0;
    p.drag = o.drag ?? 0;
    p.t = 0; p.life = o.life ?? 1;
    p.s0 = o.size ?? 1; p.s1 = o.sizeEnd ?? p.s0;
    p.c0.set(o.color ?? 0xffffff);
    p.c1.set(o.colorEnd ?? (o.color ?? 0xffffff));
    p.stretch = o.stretch ?? 0;
    p.fade = o.fade ?? 'linear';
  }

  /** this-frame-only quad (attached effects); call every frame you want it visible */
  push(x: number, y: number, z: number, size: number, color: number | Color, rot = 0): void {
    if (this.immediate.length > this.cap * 0.5) return;
    this.immediate.push({ x, y, z, s: size, c: _col.set(color).clone(), rot });
  }

  get liveCount(): number { return this.pool.reduce((n, p) => n + (p.alive ? 1 : 0), 0); }

  clear(): void {
    for (const p of this.pool) p.alive = false;
    this.immediate.length = 0;
    this.mesh.count = 0;
  }

  update(dt: number, camera: Camera): void {
    camera.getWorldQuaternion(_q);
    let n = 0;

    for (const p of this.pool) {
      if (!p.alive) continue;
      p.t += dt;
      if (p.t >= p.life) { p.alive = false; continue; }
      const k = 1 - p.drag * dt;
      p.vx *= k; p.vy *= k; p.vz *= k;
      p.vy -= p.g * dt;
      p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;

      const u = p.t / p.life;
      let a = 1 - u;
      if (p.fade === 'inOut') a = Math.sin(Math.PI * u);
      else if (p.fade === 'hold') a = u > 0.75 ? (1 - u) * 4 : 1;
      const s = p.s0 + (p.s1 - p.s0) * u;

      if (n >= this.cap) break;
      _pos.set(p.x, p.y, p.z);
      if (p.stretch > 0.001) {
        const sp = Math.hypot(p.vx, p.vy, p.vz);
        const stretch = 1 + Math.min(4, sp * p.stretch);
        _scl.set(s, s * stretch, 1);
        const ang = Math.atan2(p.vx, p.vy);
        _dummy.quaternion.copy(_q);
        _dummy.rotateZ(-ang);
        _m.compose(_pos, _dummy.quaternion, _scl);
      } else {
        _scl.set(s, s, 1);
        _m.compose(_pos, _q, _scl);
      }
      this.mesh.setMatrixAt(n, _m);
      _col.copy(p.c0).lerp(p.c1, u).multiplyScalar(a);
      this.aCol.setXYZ(n, _col.r, _col.g, _col.b);
      n++;
    }

    for (const q of this.immediate) {
      if (n >= this.cap) break;
      _pos.set(q.x, q.y, q.z);
      _scl.set(q.s, q.s, 1);
      if (q.rot !== 0) {
        _dummy.quaternion.copy(_q);
        _dummy.rotateZ(q.rot);
        _m.compose(_pos, _dummy.quaternion, _scl);
      } else {
        _m.compose(_pos, _q, _scl);
      }
      this.mesh.setMatrixAt(n, _m);
      this.aCol.setXYZ(n, q.c.r, q.c.g, q.c.b);
      n++;
    }
    this.immediate.length = 0;

    this.mesh.count = n;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.aCol.needsUpdate = true;
    }
  }
}

export { AdditiveBlending, NormalBlending };
